/**
 * Curated NEWS SOURCE registry (#182 — YouTube news reel, the cheap first cut of
 * the News & Social wire #65).
 *
 * News is an AMBIENT, operator-gated media-tile kind — NOT a map marker, NOT an
 * anomaly, and NOT a default-view layer (design council). An operator pins a news
 * outlet onto a workstation (the `kind:'news'` pinned-feed path) or it rides the
 * WALL media mosaic; precision (does this story matter?) lives at promotion, never
 * here. So this module is just a curated list of free, public YouTube-live news
 * channels plus the pure selection/lookup helpers — no scoring, no Response Level.
 *
 * Each source points at a YouTube *channel* (UC…), NOT a pinned video id, so the
 * current live broadcast is resolved AT VIEW TIME via the existing channel-live
 * resolver (`resolveChannelLiveVideoId`, shared with the camera path) — a pinned
 * video id would rot the moment the outlet restarts its stream. A 24/7 single-live
 * outlet needs no `titleMatch`; set it only for a channel that runs more than one
 * simultaneous live (the same disambiguation the camera path uses).
 *
 * NB: the channel ids below are CURATED (verified against youtube.com/channel/<id>
 * at authoring time). If an outlet isn't currently broadcasting — or an id needs
 * correction — the view-time resolver returns null and the player surfaces an
 * honest "not live" state; it never crashes the tile. Outlets like Al Jazeera
 * English / DW / France 24 / Sky News run persistent 24/7 lives; the Canadian
 * networks broadcast live around events.
 */

/** Country / region buckets used to group + filter the picker. */
export type NewsCountry = 'CA' | 'US' | 'UK' | 'FR' | 'DE' | 'INT';

export interface NewsSource {
  /** Stable slug id — the pinned-feed id AND the `/api/news/:id/youtube` param.
   *  Lowercase kebab; never a YouTube id (those rotate per outlet rebrand). */
  id: string;
  /** Outlet / brand, e.g. "Al Jazeera English". */
  outlet: string;
  /** Country/region bucket for grouping + the picker's country filter. */
  country: NewsCountry;
  /** Display label for the country group header. */
  countryLabel: string;
  /** Tile / pin caption (outlet + a "Live" suffix). */
  label: string;
  /** YouTube channel id (UC + 22 chars). Resolved to the current live videoId at
   *  view time — never a pinned video id. */
  channelId: string;
  /** Optional title-match keyword — set ONLY when a channel runs >1 simultaneous
   *  live and the right one must be disambiguated by title (camera-path semantics).
   *  Omitted for a single 24/7-live outlet. */
  titleMatch?: string;
}

const COUNTRY_LABELS: Record<NewsCountry, string> = {
  CA: 'Canada',
  US: 'United States',
  UK: 'United Kingdom',
  FR: 'France',
  DE: 'Germany',
  INT: 'International',
};

/** Display order for the country groups in the picker (Canada first — home ops). */
export const NEWS_COUNTRY_ORDER: NewsCountry[] = ['CA', 'INT', 'UK', 'FR', 'DE', 'US'];

function source(
  id: string,
  outlet: string,
  country: NewsCountry,
  channelId: string,
  titleMatch?: string,
): NewsSource {
  return {
    id,
    outlet,
    country,
    countryLabel: COUNTRY_LABELS[country],
    label: `${outlet} · Live`,
    channelId,
    ...(titleMatch ? { titleMatch } : {}),
  };
}

/**
 * The curated registry. Channel ids verified against youtube.com/channel/<id> at
 * authoring time (2026-06-09). Keep this list short + free/public only.
 */
export const NEWS_SOURCES: readonly NewsSource[] = [
  // Canada — broadcast live around major events (not always 24/7 live).
  source('cbc-news', 'CBC News', 'CA', 'UCuFFtHWoLl5fauMMD5Ww2jA'),
  source('ctv-news', 'CTV News', 'CA', 'UCi7Zk9baY1tvdlgxIML8MXg'),
  source('global-news', 'Global News', 'CA', 'UChLtXXpo4Ge1ReTEboVvTDg'),
  // International / wire — persistent 24/7 English lives (most reliable).
  source('al-jazeera-english', 'Al Jazeera English', 'INT', 'UCNye-wNBqNL5ZzHSJj3l8Bg'),
  source('france24-english', 'France 24 English', 'FR', 'UCQfwfsi5VrQ8yKZ-UWmAEFg'),
  source('dw-english', 'DW News', 'DE', 'UCknLrEdhRCp1aegoMqRaCZg'),
  source('sky-news', 'Sky News', 'UK', 'UCoMdktPbSTixAyNGwb-UYkQ'),
];

/** Look up a source by its stable slug id (the pin id / route param). */
export function findNewsSource(id: string): NewsSource | undefined {
  return NEWS_SOURCES.find((s) => s.id === id);
}

export interface NewsSourceFilter {
  /** Restrict to a single country bucket. */
  country?: NewsCountry;
  /** Case-insensitive substring match on the outlet name. */
  outlet?: string;
}

/**
 * Pure selection: filter the registry by outlet substring and/or country. Returns
 * a fresh array (registry order preserved) — empty when nothing matches. No
 * network, no scoring; this is just operator-facing browse/filter.
 */
export function selectNewsSources(filter: NewsSourceFilter = {}): NewsSource[] {
  const outlet = filter.outlet?.trim().toLowerCase();
  return NEWS_SOURCES.filter((s) => {
    if (filter.country && s.country !== filter.country) return false;
    if (outlet && !s.outlet.toLowerCase().includes(outlet)) return false;
    return true;
  });
}

export interface NewsCountryGroup {
  country: NewsCountry;
  countryLabel: string;
  sources: NewsSource[];
}

/**
 * Group sources by country in {@link NEWS_COUNTRY_ORDER} (then any extra buckets),
 * dropping empty groups. Drives the picker's grouped list. Honours a filter so the
 * grouped view and the flat {@link selectNewsSources} stay consistent.
 */
export function groupNewsSourcesByCountry(filter: NewsSourceFilter = {}): NewsCountryGroup[] {
  const selected = selectNewsSources(filter);
  const order = [
    ...NEWS_COUNTRY_ORDER,
    ...selected.map((s) => s.country).filter((c) => !NEWS_COUNTRY_ORDER.includes(c)),
  ];
  const seen = new Set<NewsCountry>();
  const groups: NewsCountryGroup[] = [];
  for (const country of order) {
    if (seen.has(country)) continue;
    seen.add(country);
    const sources = selected.filter((s) => s.country === country);
    if (sources.length === 0) continue;
    groups.push({ country, countryLabel: COUNTRY_LABELS[country], sources });
  }
  return groups;
}

/**
 * Shape a source into the `kind:'news'` pinned-feed the panel/wall consume. Kept
 * here (not in the web pin module) so the pin id↔registry contract lives with the
 * registry. Mirrors the camera pin shape: { kind, id, label }.
 */
export function newsSourceToPinnedFeed(source: NewsSource): {
  kind: 'news';
  id: string;
  label: string;
} {
  return { kind: 'news', id: source.id, label: source.label };
}
